import React, { useState, useRef, useEffect } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTenant } from '../context/TenantContext'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { usePlatform } from '../context/PlatformContext'
import moduleConfig from '../config/moduleConfig'
import {
  HomeIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  UserCircleIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  QuestionMarkCircleIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'
import DepartmentSwitcher from './DepartmentSwitcher'
import { isNative } from '../services/native'
import { activeDepartment } from '../services/departments'

function Sidebar({ hasBanner = false, hasPlatformBanner = false, collapsed = false, onToggleCollapse, onShowFeedback }) {
  const { tenant, orderedModules } = useTenant()
  const { user, logout, isPinDevice } = useAuth()
  const [switcherOpen, setSwitcherOpen] = useState(false)
  // A station device belongs to one department
  const canSwitch = isNative() && !isPinDevice
  const { selectedTenantName } = usePlatform()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    setUserMenuOpen(false)
    await logout()
  }

  // Get enabled modules for nav, using the ordered list from TenantContext
  const enabledModules = orderedModules
    .filter(m => moduleConfig[m.module_name])
    .map(m => ({
      ...moduleConfig[m.module_name],
      moduleName: m.module_name
    }))

  return (<>
    <aside className={`
      hidden lg:flex lg:flex-col fixed left-0 z-50 glass-surface border-r border-slate-700
      transition-all duration-300 ease-in-out safe-top
      ${hasPlatformBanner && hasBanner ? 'top-[4.5rem] h-[calc(100%-4.5rem)]' : hasPlatformBanner ? 'top-9 h-[calc(100%-2.25rem)]' : hasBanner ? 'top-9 h-[calc(100%-2.25rem)]' : 'top-0 h-full'}
      ${collapsed ? 'w-16' : 'w-64'}
    `}>
      {/* Logo/Header */}
      <div className={`h-16 shrink-0 flex items-center border-b border-slate-700 ${collapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
        <div className={`flex items-center ${collapsed ? '' : 'gap-3'}`}>
          <img src="/images/scenelight-logo.png" alt="Scenelight" className="w-10 h-10 flex-shrink-0" />
          {!collapsed && (
            <div>
              <h1 className="text-white text-lg tracking-wider" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                SCENELIGHT
              </h1>
              <p className="text-xs text-slate-400">
                {selectedTenantName || tenant?.name || 'Fire Department'}
              </p>
            </div>
          )}
        </div>

      </div>


      {/* Navigation */}
      <nav className={`flex-1 min-h-0 overflow-y-auto p-2 space-y-1 ${collapsed ? 'px-2' : 'p-4'}`}>
        {/* Dashboard - always visible */}
        <NavLink
          to="/"
          end
          className={({ isActive }) => `
            flex items-center rounded-lg font-medium transition-colors
            ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'}
            ${isActive
              ? 'bg-amber-500/20 text-amber-500'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }
          `}
          title={collapsed ? 'Dashboard' : undefined}
        >
          <HomeIcon className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Dashboard</span>}
        </NavLink>

        {/* Module Links */}
        {enabledModules.length > 0 && (
          <>
            {collapsed ? (
              <div className="pt-2 pb-1">
                <div className="border-t border-slate-700 mx-1"></div>
              </div>
            ) : (
              <div className="pt-4 pb-2">
                <p className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Modules
                </p>
              </div>
            )}

            {enabledModules.map(module => {
              const isActive = location.pathname === module.path || location.pathname.startsWith(module.path + '/')
              return (
                <button
                  key={module.moduleName}
                  onClick={() => {
                    if (isActive) {
                      // Already on this module — force full reload to reset view state
                      window.location.href = module.path
                    } else {
                      navigate(module.path)
                    }
                  }}
                  className={`
                    flex items-center rounded-lg font-medium transition-colors w-full text-left
                    ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'}
                    ${isActive
                      ? 'bg-amber-500/20 text-amber-500'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }
                  `}
                  title={collapsed ? module.name : undefined}
                >
                  <module.Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span>{module.name}</span>}
                </button>
              )
            })}
          </>
        )}

        {/* Settings - always visible at bottom */}
        {collapsed ? (
          <div className="pt-2 pb-1">
            <div className="border-t border-slate-700 mx-1"></div>
          </div>
        ) : (
          <div className="pt-4 pb-2">
            <p className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Account
            </p>
          </div>
        )}

        <NavLink
          to="/settings"
          className={({ isActive }) => `
            flex items-center rounded-lg font-medium transition-colors
            ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'}
            ${isActive
              ? 'bg-amber-500/20 text-amber-500'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }
          `}
          title={collapsed ? 'Settings' : undefined}
        >
          <Cog6ToothIcon className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Settings</span>}
        </NavLink>
      </nav>

      {/* User menu, Feedback & Collapse toggle at bottom. In the column below the
          menu (not pinned over it), so a short window scrolls the menu instead
          of hiding items under this. */}
      <div className="shrink-0 border-t border-slate-700">
        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={`w-full flex items-center text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'}`}
            title={collapsed ? (user?.name || user?.email || 'User') : undefined}
          >
            <div className="w-7 h-7 rounded-full bg-amber-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || '?'}
            </div>
            {!collapsed && <span className="text-sm truncate">{user?.name || user?.email || 'User'}</span>}
          </button>

          {userMenuOpen && (
            <div className={`absolute bottom-full mb-1 w-56 bg-slate-800 rounded-lg shadow-lg border border-slate-700 py-1 z-50 ${collapsed ? 'left-full ml-1' : 'left-2'}`}>
              <div className="px-4 py-3 border-b border-slate-700">
                <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-400">{user?.email}</p>
              </div>
              <div className="py-1">
                <a href="/settings/profile" className="flex items-center gap-3 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700">
                  <UserCircleIcon className="w-4 h-4" />
                  Profile Settings
                </a>
                <a href="/settings/notifications" className="flex items-center gap-3 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700">
                  <BellIcon className="w-4 h-4" />
                  Notifications
                </a>
                <button
                  onClick={() => { toggleTheme(); setUserMenuOpen(false) }}
                  className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </button>
              </div>
              <div className="border-t border-slate-700 py-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Help link */}
        <NavLink
          to="/help"
          className={({ isActive }) => `w-full flex items-center transition-colors ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'} ${isActive ? 'text-amber-500' : 'text-slate-400 hover:text-amber-400 hover:bg-slate-700/50'}`}
          title={collapsed ? 'Help' : undefined}
        >
          <QuestionMarkCircleIcon className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm">Help</span>}
        </NavLink>

        {/* Department switcher (native app) */}
        {canSwitch && (
          <button
            onClick={() => setSwitcherOpen(true)}
            className={`w-full flex items-center text-slate-400 hover:text-amber-400 hover:bg-slate-700/50 transition-colors ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'}`}
            title={collapsed ? 'Switch Department' : undefined}
          >
            <BuildingOffice2Icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm truncate">{activeDepartment()?.name || 'Switch Department'}</span>}
          </button>
        )}
        {switcherOpen && <DepartmentSwitcher onClose={() => setSwitcherOpen(false)} />}

        {/* Feedback button */}
        <button
          onClick={onShowFeedback}
          className={`w-full flex items-center text-slate-400 hover:text-amber-400 hover:bg-slate-700/50 transition-colors ${collapsed ? 'justify-center p-3' : 'gap-3 px-4 py-2.5'}`}
          title={collapsed ? 'Provide Feedback' : undefined}
        >
          <ChatBubbleOvalLeftEllipsisIcon className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm">Provide Feedback</span>}
        </button>

        {/* Collapse toggle - desktop only */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex w-full items-center justify-center p-3 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRightIcon className="w-5 h-5" />
          ) : (
            <div className="flex items-center gap-2">
              <ChevronLeftIcon className="w-5 h-5" />
              <span className="text-xs">Collapse</span>
            </div>
          )}
        </button>
        {!collapsed && (
          <p className="text-xs text-slate-500 text-center pb-3">
            Scenelight v1.0.0
          </p>
        )}
      </div>

    </aside>

  </>
  )
}

export default Sidebar
